import * as React from 'react';
import Table from 'react-bootstrap/Table';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import GroupAddOutlinedIcon from '@mui/icons-material/GroupAddOutlined';
import Pagination from '@mui/material/Pagination';
import { NavLink } from 'react-router-dom';
import Col from 'react-bootstrap/Col';
import Form from 'react-bootstrap/Form';
import Row from 'react-bootstrap/Row';
import { postRequest } from '../../lib/api';
import { AuthContext } from '../../authContext/context';
import { useContext, useEffect, useState, useRef } from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Swal from 'sweetalert2';
import BreadcrumbsWrapper from '../../components/breadcrumbs';
import { checkPermission } from '../../utils/checkPermissions';

export default function UserGroups() {
  const [groupList, setGroupList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalDocs, setTotalDocs] = useState(0);
  const [searchName, setSearchName] = useState('');

  const authCtx = useContext(AuthContext);
  const userPermissions = authCtx.permissions;
  const canManageGroups = checkPermission(userPermissions, 'settings', 'manage_user_groups');
  const debounceRef = useRef();

  const fetchUserGroups = async (searchNameVal, pageVal = page) => {
    setLoading(true);
    try {
      const payload = {
        page: pageVal,
        limit: 5,
        title: searchNameVal,
      };

      const response = await postRequest('private/userGroupList', payload, authCtx.token);
      if (response?.status === 200 && response?.data) {
        setGroupList(response.data.docs || []);
        setTotalPages(response.data.totalPages || 1);
        setTotalDocs(response.data.totalDocs || 0);
      } else {
        setGroupList([]);
        setTotalPages(1);
        setTotalDocs(0);
      }
    } catch (err) {
      setGroupList([]);
      setTotalPages(1);
      setTotalDocs(0);
    }
    setLoading(false);
  };

  // Fetch on mount and when page changes
  useEffect(() => {
    fetchUserGroups(searchName, page);
    // eslint-disable-next-line
  }, [page, authCtx.token]);

  // Debounced search on name change
  useEffect(() => {
    setPage(1); // Reset to first page on search
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      fetchUserGroups(searchName, 1);
    }, 400);
    // eslint-disable-next-line
  }, [searchName]);

  const handlePageChange = (_event, value) => {
    setPage(value);
  };

  const handleDelete = async (groupId) => {
    const result = await Swal.fire({
      title: 'Are you sure?',
      text: 'You will not be able to recover this user group!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#bb0000',
      cancelButtonColor: '#aaa',
      confirmButtonText: 'Yes, delete it!',
    });

    if (result.isConfirmed) {
      try {
        const response = await postRequest(`private/deleteUserGroup/${groupId}`, {}, authCtx.token);
        if (response?.status === 200) {
          Swal.fire('Deleted!', 'User group has been deleted.', 'success');

          const payload = {
            page,
            limit: 5,
            title: searchName,
          };
          const listResponse = await postRequest('private/userGroupList', payload, authCtx.token);
          const docs = listResponse?.data?.docs || [];
          const totalPagesAfterDelete = listResponse?.data?.totalPages || 1;

          if (docs.length === 0 && page > 1) {
            setPage((prev) => prev - 1);
          } else {
            setGroupList(docs);
            setTotalPages(totalPagesAfterDelete);
          }
        } else {
          Swal.fire('Failed!', response?.message || 'Something went wrong.', 'error');
        }
      } catch (err) {
        Swal.fire('Error!', err?.message || 'Something went wrong.', 'error');
      }
    }
  };

  return (
    <>
      <BreadcrumbsWrapper
        items={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'User Groups', path: '' },
        ]}
      />
      <br></br>
      <div className='page-header'>
        <h1 className='mt-0'>User Groups</h1>
        {canManageGroups && (
          <div className='header-right-btn'>
            <Stack spacing={2} direction="row">
              <Button variant="outlined" className='red-outline-btn text-capitalize'>
                <NavLink to="/user-groups/create" className="red-link-text">
                  <GroupAddOutlinedIcon /> &nbsp; Add New User Group
                </NavLink>
              </Button>
            </Stack>
          </div>
        )}
      </div>
      <div className='page-top-formview mb-3'>
        <Form>
          <Row className="mb-3">
            <Form.Group as={Col} controlId="searchName">
              <Form.Label>Group Name</Form.Label>
              <Form.Control
                type="text"
                placeholder="Enter group name"
                value={searchName}
                onChange={e => {
                  setSearchName(e.target.value);
                  setPage(1);
                }}
              />
            </Form.Group>
          </Row>
        </Form>
      </div>

      <div>
        <Table striped responsive bordered>
          <thead>
            <tr>
              <th>Group Name</th>
              <th>Default Group</th>
              <th>Total Users</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center' }}>
                  <CircularProgress size={28} />
                </td>
              </tr>
            ) : groupList.length === 0 ? (
              <tr>
                <td colSpan={4} style={{ textAlign: 'center' }}>
                  No user groups found.
                </td>
              </tr>
            ) : (
              groupList.map((group) => (
                <tr key={group._id}>
                  <th>{group?.title}</th>
                  <td>{group?.is_default ? 'Yes' : 'No'}</td>
                  <td>{group?.userCount ?? 0}</td>
                  <td>
                    <span
                      className='red-link-text cursor-pointer w-18'
                      title="View"
                      style={{ marginRight: 8 }}
                    >
                      <NavLink to={`/user-groups/view/${group._id}`} className="red-link-text">
                        <VisibilityOutlinedIcon />
                      </NavLink>
                    </span>
                    <span
                      className='red-link-text cursor-pointer w-18'
                      title="Edit"
                      style={{ marginRight: 8 }}
                    >
                      <NavLink to={`/user-groups/edit/${group._id}`} className="red-link-text">
                        <EditOutlinedIcon />
                      </NavLink>
                    </span>
                    <span
                      className='red-link-text cursor-pointer w-18'
                      title="Delete"
                      onClick={() => handleDelete(group._id)}
                    >
                      <DeleteOutlineOutlinedIcon />
                    </span>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </Table>
        {/* Pagination */}
        {groupList.length > 0 && (
          <>
            <div style={{ display: 'flex', justifyContent: 'flex-start', marginTop: 8, marginBottom: 8 }}>
              <span className="text-muted small">
                Showing {((page - 1) * 5) + 1}-{Math.min(page * 5, totalDocs)} of {totalDocs} user groups
              </span>
            </div>
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
                shape="rounded"
                size="medium"
                sx={{
                  '& .Mui-selected': {
                    background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%) !important',
                    color: '#fff !important',
                  },
                  '& .MuiPaginationItem-root': {
                    color: 'var(--bs-red-color)',
                    borderColor: 'var(--bs-red-color)',
                  },
                }}
              />
            </div>
          </>
        )}
      </div>
    </>
  );
}
